import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { configRepositoryApi } from '@/lib/api/config-repository.api';

const QK = (configId: string) => ['config-repository', configId];

export function useConfigBranches(configId: string | undefined) {
  return useQuery({
    queryKey: [...QK(configId ?? ''), 'branches'],
    queryFn: () => configRepositoryApi.branches(configId!),
    enabled: !!configId,
  });
}

export function useConfigCommits(configId: string | undefined, branch = 'main', limit = 50) {
  return useQuery({
    queryKey: [...QK(configId ?? ''), 'commits', branch, limit],
    queryFn: () => configRepositoryApi.commits(configId!, branch, limit),
    enabled: !!configId,
  });
}

export function useConfigSnapshot(configId: string | undefined, commitId: string | null) {
  return useQuery({
    queryKey: [...QK(configId ?? ''), 'snapshot', commitId],
    queryFn: () => configRepositoryApi.snapshot(configId!, commitId!),
    enabled: !!configId && !!commitId,
  });
}

export function useConfigDiff(configId: string | undefined, from?: string, to?: string) {
  return useQuery({
    queryKey: [...QK(configId ?? ''), 'diff', from, to],
    queryFn: () => configRepositoryApi.diff(configId!, from!, to!),
    enabled: !!configId && !!from && !!to,
  });
}

export function usePromoteBranch(configId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (branchId: string) => configRepositoryApi.promote(configId, branchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK(configId) });
      qc.invalidateQueries({ queryKey: ['configurations', configId] });
    },
  });
}

export function useAbandonBranch(configId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (branchId: string) => configRepositoryApi.abandon(configId, branchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK(configId) });
      qc.invalidateQueries({ queryKey: ['configurations', configId] });
    },
  });
}

export function useRollbackConfig(configId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (toCommitId: string) => configRepositoryApi.rollback(configId, toCommitId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK(configId) });
      qc.invalidateQueries({ queryKey: ['configurations', configId] });
    },
  });
}
